/**
 * SRM site content — Master Design Specification.
 * Edit business details, team, services and copy from this single file.
 */

export const site = {
    business: {
        name: "SRM",
        fullName: "Strata Roof Management Australia",
        tagline: "Assess \u00b7 Comply \u00b7 Protect",
        position: "Independent Roof Asset Management for Strata Communities",
        website: "strataroofmanagement.com.au",
        email: "info@strataroofmanagement.com.au",
        phones: [
            { name: "Amanda Belot", role: "Business Development Manager", number: "0407 053 267" },
            { name: "Andy Nagy", role: "General Manager", number: "0497 372 914" },
        ],
        office: "Sunshine Coast, Queensland",
        serviceArea: "Australia-wide",
        responseTime: "Within 1 business day",
        abn: "",
        social: {},
    },

    /* Simplified nav — 6 items per v2.0 corporate release brief.
       Why Roofs Fail remains a page (linked from Guardianship intro)
       but is dropped from primary nav to reduce top-level count.  */
    nav: [
        { label: "Home", href: "/" },
        { label: "About SRM", href: "/about" },
        { label: "Services", href: "/services" },
        { label: "Guardian Program", href: "/guardian-program" },
        { label: "Resources", href: "/resources" },
        { label: "Contact", href: "/contact" },
    ],

    hero: {
        tagline: "Assess \u00b7 Comply \u00b7 Protect",
        headline: "Independent Roof Experts.",
        headlineLines: [
            "Independent Roof Experts.",
            "Guardians of Your Roof.",
        ],
        sub: "",
        primaryCta: { label: "Speak with the SRM Team", href: "/contact" },
        secondaryCta: { label: "Explore Our Services", href: "/services" },
        image: "/assets/hero-background.png",
    },

    trust: [
        {
            title: "Independent Advice",
            body: "Advice-only practice with structurally impartial counsel.",
            icon: "ShieldCheck",
        },
        {
            title: "Builder-Level Expertise",
            body: "Trade-qualified inspection professionals.",
            icon: "HardHat",
        },
        {
            title: "Australia Wide",
            body: "Local knowledge. National reach.",
            icon: "MapPin",
        },
        {
            title: "Detailed Reporting",
            body: "Clear, factual and actionable.",
            icon: "FileText",
        },
    ],

    /* Home "How SRM Works" — the six-stage independent journey per v2.0 brief:
       Assess \u2192 Report \u2192 Scope \u2192 Tender \u2192 Oversee \u2192 Monitor.
       Rendered as a compact horizontal process graphic. */
    howItWorks: {
        eyebrow: "The SRM Process",
        steps: [
            { title: "Assess",   icon: "Search",         body: "Independent roof or external building condition assessment." },
            { title: "Report",   icon: "FileText",       body: "Clear evidence, photos, risks and prioritised recommendations." },
            { title: "Scope",    icon: "ClipboardList",  body: "Independent scope of works defining exactly what is required." },
            { title: "Tender",   icon: "Users",          body: "Genuinely comparable contractor submissions against one scope." },
            { title: "Oversee",  icon: "Eye",            body: "Independent oversight of delivery, quality, variations and claims." },
            { title: "Monitor",  icon: "CalendarClock",  body: "Annual assessments and long-term Guardianship of the asset." },
        ],
    },

    whyCommittees: {
        eyebrow: "Why Independence Matters",
        headline: "Independent advice, purely focused on your roof.",
        image: "/assets/srm-meeting.png",
        paragraphs: [
            "SRM is engaged for one thing: independent professional judgement on the condition of strata roofs and external building elements.",
            "Our practice is advice-only \u2014 so every recommendation is based on the evidence and shaped entirely by what your building genuinely needs.",
            "That's what independence looks like in practice \u2014 and why body corporate managers and committees engage SRM.",
        ],
    },

    /* Home service cards \u2014 the two headline offerings.
       Roof Assessments (established) + External Building Assessments (new). */
    services: [
        {
            slug: "roof-assessments",
            title: "Roof Assessments",
            blurb: "Independent roof condition assessments, professional reporting and long-term roof asset guidance.",
            cta: "Explore Roof Assessments",
            icon: "Search",
        },
        {
            slug: "external-building-assessments",
            title: "External Building Assessments",
            blurb: "Independent assessments of external building elements, defects and maintenance priorities.",
            cta: "Explore Building Assessments",
            icon: "Building2",
        },
        {
            slug: "scope-and-tender",
            title: "Scope of Works & Tender",
            blurb: "Independent scopes that let contractors price the same work on a comparable basis.",
            cta: "Explore Procurement Support",
            icon: "ClipboardList",
        },
        {
            slug: "delivery-oversight",
            title: "Delivery Oversight",
            blurb: "Independent oversight through delivery \u2014 quality, variations, claims, completion.",
            cta: "Explore Delivery Oversight",
            icon: "Eye",
        },
        {
            slug: "guardianship-program",
            title: "Guardianship Program",
            blurb: "Ongoing annual assessments and long-term monitoring of the roof asset.",
            cta: "Explore Guardianship",
            icon: "ShieldCheck",
        },
        {
            slug: "professional-reporting",
            title: "Professional Reporting",
            blurb: "Clear condition summary, photographic evidence, prioritised actions \u2014 committee-ready.",
            cta: "Explore Reporting",
            icon: "FileText",
        },
    ],

    /* Four Independence Benefits used in the "Why Independence Matters" section. */
    independence: [
        {
            title: "Independent Advice",
            body: "Evidence-based recommendations without contractor influence.",
            icon: "ShieldCheck",
        },
        {
            title: "Advice-Only Practice",
            body: "SRM earns its income from professional advice, so every recommendation is guided by what the building genuinely needs.",
            icon: "Coins",
        },
        {
            title: "Confident Decisions",
            body: "Clear, practical information that helps committees make informed decisions.",
            icon: "Eye",
        },
        {
            title: "Better Tender Outcomes",
            body: "Defined scopes allow comparable contractor pricing and more confident selection.",
            icon: "ClipboardCheck",
        },
    ],

    /* Six \"Why SRM\" evidence blocks. */
    whyChoose: [
        { title: "100% Independent",         body: "Advice-only practice \u2014 professional counsel is our sole deliverable.",             icon: "ShieldCheck" },
        { title: "Builder-Level Experience", body: "Practical construction and roofing knowledge on every job.", icon: "HardHat" },
        { title: "Roof & Building Capability", body: "Independent roof and external building assessments.",      icon: "Building2" },
        { title: "Professional Reporting",   body: "Clear evidence, priorities and recommendations.",           icon: "FileText" },
        { title: "Procurement Support",      body: "Defined scopes and comparable contractor submissions.",     icon: "ClipboardList" },
        { title: "Ongoing Guardianship",     body: "Long-term monitoring, planning and committee support.",     icon: "CalendarClock" },
    ],

    callouts: {
        cost: {
            eyebrow: "",
            headline: "The cost isn\u2019t always the repair.",
            body: "Reactive scopes can inflate costs and repeat the same failures. An independent scope of works helps committees sequence spend and avoid unnecessary works.",
            link: { label: "Learn more", href: "/services" },
            icon: "Coins",
        },
        guardianship: {
            eyebrow: "",
            headline: "The assessment is the starting point.",
            body: "The Guardianship Program keeps SRM beside the client through scope of works, tender, delivery oversight and long-term monitoring \u2014 not just the first report.",
            bullets: [
                "Assessment & Report",
                "Scope of Works & Tender",
                "Delivery Oversight",
                "Ongoing Guardianship & Monitoring",
            ],
            link: { label: "Explore the Guardianship Journey", href: "/guardian-program" },
            icon: "ShieldCheck",
        },
    },

    /* Team \u2014 mirrors /pages/Team.jsx bios & photos so /about and /home
       stay in sync. Local jpg assets, not stock photography. */
    team: [
        {
            name: "Andy Nagy",
            role: "General Manager",
            bio:
                "Leads SRM's day-to-day operations. Background across construction project delivery, contractor management and strata advisory work \u2014 the accountability link between fieldwork, reporting and committee outcomes.",
            image: "/assets/team/andy.jpg",
        },
        {
            name: "Amanda Belot",
            role: "Business Development Manager",
            bio:
                "First point of contact for strata committees, building managers and strata managers engaging SRM. Deep relationship-management experience across the strata sector \u2014 every enquiry scoped accurately and matched to the right service.",
            image: "/assets/team/amanda.jpg",
        },
        {
            name: "Joe Jenkins",
            role: "Roof Assessment Lead",
            bio:
                "Heads SRM's on-site assessment program. Hands-on roofing and building experience across residential, commercial and high-density strata assets \u2014 a builder's understanding of how roof systems fail, translated into evidence committees can act on.",
            image: "/assets/team/joe.jpg",
        },
    ],

    closing: {
        headline: "Bring your roof under independent care.",
        sub: "One conversation is enough to work out whether SRM is the right partner for your building.",
        primaryCta: { label: "Speak with the SRM Team", href: "/contact" },
    },

    footer: {
        about:
            "Independent Roof and External Building Assessments for Australian strata communities. Assess \u00b7 Comply \u00b7 Protect.",
        quickLinks: [
            { label: "Home", href: "/" },
            { label: "About SRM", href: "/about" },
            { label: "Services", href: "/services" },
            { label: "Guardianship Program", href: "/guardian-program" },
            { label: "Resources", href: "/resources" },
            { label: "Speak with the SRM Team", href: "/contact" },
        ],
        servicesLinks: [
            { label: "Roof Assessments", href: "/services#roof-assessments" },
            { label: "External Building Assessments", href: "/services#external-building-assessments" },
            { label: "Scope of Works & Tender", href: "/services#scope-and-tender" },
            { label: "Delivery Oversight", href: "/services#delivery-oversight" },
            { label: "Guardianship Program", href: "/guardian-program" },
            { label: "Professional Reporting", href: "/services#professional-reporting" },
        ],
        ctaCard: {
            heading: "Ready to speak with the team?",
            body: "One conversation is enough to understand whether SRM is the right independent partner for your building.",
            cta: { label: "Speak with the SRM Team", href: "/contact" },
        },
        legal: [
            { label: "Privacy Policy", href: "/privacy" },
            { label: "Terms & Conditions", href: "/terms" },
        ],
    },

    servicesPage: {
        volume: "Volume 02",
        eyebrow: "Services",
        headline:
            "Independent technical advice for every stage of a strata roof asset.",
        lede:
            "From statutory inspection through to lifecycle planning and on-site delivery \u2014 a single, joined-up engagement built for committees, brokers and strata managers.",
        heroImage:
            "https://images.unsplash.com/photo-1768677432858-b9a8608dd849",
        heroMeta: [
            { label: "Discipline", value: "Strata Roof Consultancy" },
            { label: "Coverage", value: "Australia-wide" },
            { label: "Practice", value: "Independent \u00b7 Builder-led" },
        ],
        introHeading: "An independent practice across the roof lifecycle.",
        introBody:
            "SRM is engaged by strata committees, owners corporations, brokers and facility managers to bring independent technical clarity to roof assets. Because our practice is advice-only, every report, scope and lifecycle plan is written for the asset itself \u2014 delivering counsel your building can rely on.",
        introAside: [
            { value: "1,200+", label: "Strata buildings assessed" },
            { value: "8", label: "States & territories" },
            { value: "10-yr", label: "Standard planning horizon" },
        ],
        chapters: [
            {
                index: "01",
                slug: "compliance-roof-assessments",
                title: "Compliance Roof Assessments",
                kicker: "Inspect",
                description:
                    "Statutory assessments aligned to Australian Standards, BCA / NCC and the original building approval \u2014 issued as defensible written reports.",
                stat: "AS 4654.2",
                statLabel: "Standards aligned",
                image:
                    "https://images.pexels.com/photos/8312917/pexels-photo-8312917.jpeg",
            },
            {
                index: "02",
                slug: "roof-condition-assessments",
                title: "Roof Condition Assessments",
                kicker: "Inspect",
                description:
                    "Independent condition reporting with drone survey, defect register, fixings audit and prioritised remediation pathways for the committee.",
                stat: "Drone-surveyed",
                statLabel: "CASA-licensed pilots",
                image:
                    "https://images.pexels.com/photos/8356689/pexels-photo-8356689.jpeg",
            },
            {
                index: "03",
                slug: "insurance-assessments",
                title: "Insurance Assessments",
                kicker: "Assess",
                description:
                    "Storm, hail and water-ingress assessments engineered for insurers and brokers \u2014 clear, defensible scopes that accelerate accepted claims.",
                stat: "21 days",
                statLabel: "Median claim acceptance",
                image:
                    "https://images.unsplash.com/photo-1551783841-0271a5f7c868",
            },
            {
                index: "04",
                slug: "maintenance-planning",
                title: "Maintenance Planning",
                kicker: "Maintain",
                description:
                    "Cyclical maintenance programs that protect the asset, reduce reactive call-outs and extend the service life of strata roof systems.",
                stat: "62%",
                statLabel: "Reactive call-outs reduced",
                image:
                    "https://images.pexels.com/photos/8735111/pexels-photo-8735111.jpeg",
            },
            {
                index: "05",
                slug: "capital-works-advice",
                title: "Capital Works Advice",
                kicker: "Plan",
                description:
                    "Ten-year capital works forecasting and independent scopes for major roof renewals \u2014 protecting sinking funds and AGM decisions.",
                stat: "10-year",
                statLabel: "Forecasting horizon",
                image:
                    "https://images.pexels.com/photos/36292200/pexels-photo-36292200.jpeg",
            },
            {
                index: "06",
                slug: "roof-project-management",
                title: "Roof Project Management",
                kicker: "Deliver",
                description:
                    "Tender management, contract administration and on-site oversight of major strata roof replacements \u2014 from procurement to defects liability close-out.",
                stat: "End-to-end",
                statLabel: "Tender to handover",
                image:
                    "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7",
            },
        ],
        lifecycle: [
            { step: "I", name: "Inspect", blurb: "Compliance and condition assessments \u2014 the evidence base." },
            { step: "II", name: "Assess", blurb: "Insurer-aligned damage and warranty assessments." },
            { step: "III", name: "Plan", blurb: "Capital works forecasting and lifecycle costing." },
            { step: "IV", name: "Deliver", blurb: "Tender management and on-site project oversight." },
            { step: "V", name: "Maintain", blurb: "Cyclical programs that protect the asset long-term." },
        ],
        reasons: [
            { title: "Independent advice", body: "Our reports are written for the asset and the committee \u2014 so recommendations serve your building first." },
            { title: "Builder-led expertise", body: "Trade-qualified inspectors who understand exactly how roof systems are built and how they fail." },
            { title: "Insurance fluency", body: "Damage translated into the language insurers respond to, with the evidence to back it up." },
            { title: "National capability", body: "One point of contact, consistent reporting, and inspectors on the ground in every Australian state." },
            { title: "Evidence-based reporting", body: "Drone imagery, moisture readings, fixings audits and condition mapping \u2014 not opinions." },
            { title: "Lifecycle thinking", body: "Every assessment fits a 10-year asset plan \u2014 so AGMs and sinking funds work with, not against, the roof." },
        ],
        closing: {
            eyebrow: "Next step",
            headline: "Begin with a brief \u2014 or explore the practice.",
            sub: "Most engagements start with a 20-minute discovery call. From there we propose a tailored scope, timeline and indicative fee.",
            primaryCta: { label: "Book an assessment", href: "/contact" },
            secondaryCta: { label: "Explore case studies", href: "/#case-studies" },
        },
    },
};

export const serviceOptions = [
    "Guardian Assessment",
    "Guardian Program (ongoing)",
    "Compliance & Risk",
    "Insurance Support",
    "Lifecycle Planning",
    "Project Oversight",
    "Not sure \u2014 please advise",
];

export const propertyTypeOptions = [
    "Strata / Owners Corporation",
    "Body Corporate",
    "Commercial Property",
    "Mixed-use Development",
    "Insurance / Broker",
    "Facility Manager",
    "Other",
];
